import { useState, useRef, useEffect, useLayoutEffect } from 'react'

/**
 * Tooltip & DescriptionPopover — Componentes de tooltip para iconos IC.
 * Spec: docs/superpowers/specs/2026-05-19-icon-tooltips-pills-flag-dropdown.md
 */

/**
 * TooltipBubble — sub-componente compartido de rendering.
 * Posicionado con position:fixed, auto-flip vertical via useLayoutEffect.
 * Mide ancho real (offsetWidth) para clamp horizontal correcto con cualquier unidad CSS.
 */
function TooltipBubble({ x, y, title, body, placement = 'auto', maxWidth = '280px' }) {
  const bubbleRef = useRef(null)
  const [resolvedTop, setResolvedTop] = useState(y)
  const [resolvedLeft, setResolvedLeft] = useState(x)

  useLayoutEffect(() => {
    if (!bubbleRef.current) return
    const h = bubbleRef.current.offsetHeight
    const w = bubbleRef.current.offsetWidth
    const vh = window.innerHeight
    const vw = window.innerWidth
    const margin = 8

    // Decisión vertical: auto-flip si no cabe arriba
    let p = placement
    let top
    if (p === 'auto') {
      if (y - h - margin >= margin) {
        p = 'top'
      } else if (y + h + margin <= vh - margin) {
        p = 'bottom'
      } else {
        p = 'top' // Fallback: pegar al top con margen
      }
    }
    if (p === 'top') {
      top = Math.max(margin, y - h - 10)
    } else {
      top = Math.min(vh - h - margin, y + 10)
    }

    // Clamp horizontal: usar ancho real del bubble (offsetWidth) para soportar cualquier unidad CSS
    let left = x - w / 2
    if (left < margin) left = margin
    if (left + w > vw - margin) left = vw - w - margin

    setResolvedTop(top)
    setResolvedLeft(left)
  }, [x, y, placement])

  return (
    <div
      ref={bubbleRef}
      className="fixed z-[9999] tooltip-fadein pointer-events-none"
      style={{ top: resolvedTop, left: resolvedLeft, maxWidth }}
      role="tooltip"
    >
      <div className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg shadow-xl border border-gray-300 dark:border-gray-700 px-3 py-2 text-sm">
        <div className="font-semibold mb-0.5">{title}</div>
        {body && <div className="text-gray-600 dark:text-gray-300 leading-snug">{body}</div>}
      </div>
    </div>
  )
}

/**
 * Tooltip — wrapper con hover. Para uso en modales y panel.
 *
 * Props:
 *   title: string (requerido)
 *   body: string (opcional)
 *   delay: ms antes de mostrar (default 300)
 *   maxWidth: CSS max-width (default '280px')
 *   placement: 'top' | 'bottom' | 'auto' (default 'auto')
 *   children: ReactNode (el trigger)
 */
export function Tooltip({ title, body, delay = 300, maxWidth = '280px', placement = 'auto', children }) {
  const [show, setShow] = useState(false)
  const [pos, setPos] = useState({ x: 0, y: 0 })
  const triggerRef = useRef(null)
  const timerRef = useRef(null)

  const handleEnter = () => {
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      if (triggerRef.current) {
        const r = triggerRef.current.getBoundingClientRect()
        setPos({ x: r.left + r.width / 2, y: r.top })
        setShow(true)
      }
    }, delay)
  }

  const handleLeave = () => {
    if (timerRef.current) clearTimeout(timerRef.current)
    setShow(false)
  }

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current) }, [])

  return (
    <span
      ref={triggerRef}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      className="inline-flex"
    >
      {children}
      {show && <TooltipBubble x={pos.x} y={pos.y} title={title} body={body} placement={placement} maxWidth={maxWidth} />}
    </span>
  )
}

/**
 * DescriptionPopover — variante controlada externamente. Para right-click en grilla.
 *
 * Props:
 *   x: number (viewport coord X)
 *   y: number (viewport coord Y)
 *   title: string
 *   body: string
 *   onClose: () => void  (se llama en click afuera o Esc)
 */
export function DescriptionPopover({ x, y, title, body, onClose }) {
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
      }
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [onClose])

  return (
    <>
      <div className="fixed inset-0 z-[9998]" onClick={onClose} />
      <TooltipBubble x={x} y={y} title={title} body={body} placement="auto" />
    </>
  )
}
